import { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import toast from 'react-hot-toast';
import { Header } from '../components/Header';

interface DashboardStats {
    contacts: number;
    messagesSent: number;
    connections: number;
}

interface MessagePerDay {
    date: string;
    count: number;
}

interface Campaign {
    id: string;
    nome: string;
    status: string;
    criadoEm: string;
    totalContacts: number;
    sentCount: number;
    failedCount: number;
    startedAt: string | null;
    completedAt: string | null;
}

export function DashboardPage() {
    const [stats, setStats] = useState<DashboardStats | null>(null);
    const [messagesPerDay, setMessagesPerDay] = useState<MessagePerDay[]>([]);
    const [recentCampaigns, setRecentCampaigns] = useState<Campaign[]>([]);
    const [loading, setLoading] = useState(true);

    const loadDashboardData = useCallback(async () => {
        try {
            setLoading(true);

            const [statsData, messagesData, campaignsData] = await Promise.all([
                api.getDashboardStats(),
                api.getMessagesPerDay(),
                api.getRecentCampaigns()
            ]);

            setStats(statsData);
            setMessagesPerDay(messagesData);
            setRecentCampaigns(campaignsData);
        } catch (error) {
            console.error('Erro ao carregar dados do dashboard:', error);
            const errorMessage = error instanceof Error ? error.message : 'Erro ao carregar dados do dashboard';
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadDashboardData();
    }, [loadDashboardData]);

    const getMaxCount = () => {
        if (messagesPerDay.length === 0) return 10;
        const max = Math.max(...messagesPerDay.map(m => m.count));
        return max > 0 ? max : 10;
    };

    const formatDate = (dateString: string) => {
        try {
            const date = new Date(dateString);
            const day = date.getDate().toString().padStart(2, '0');
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            return `${day}/${month}`;
        } catch {
            return dateString;
        }
    };

    const formatDateTime = (dateString: string) => {
        try {
            const date = new Date(dateString);
            const day = date.getDate().toString().padStart(2, '0');
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const year = date.getFullYear();
            const hours = date.getHours().toString().padStart(2, '0');
            const minutes = date.getMinutes().toString().padStart(2, '0');
            return `${day}/${month}/${year} às ${hours}:${minutes}`;
        } catch {
            return dateString;
        }
    };

    const getStatusColor = (status: string) => {
        const statusColors: { [key: string]: string } = {
            'COMPLETED': 'bg-green-500/20 text-green-300 border border-green-500/30',
            'RUNNING': 'bg-blue-500/20 text-blue-300 border border-blue-500/30',
            'PENDING': 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30',
            'FAILED': 'bg-red-500/20 text-red-300 border border-red-500/30',
            'PAUSED': 'bg-gray-500/20 text-gray-300 border border-gray-500/30'
        };
        return statusColors[status] || 'bg-gray-500/20 text-gray-300 border border-gray-500/30';
    };

    const getStatusLabel = (status: string) => {
        const statusLabels: { [key: string]: string } = {
            'COMPLETED': 'Concluída',
            'RUNNING': 'Em execução',
            'PENDING': 'Pendente',
            'FAILED': 'Falhou',
            'PAUSED': 'Pausada'
        };
        return statusLabels[status] || status;
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-[#1e3a5f] to-black">
                <Header title="Dashboard" compact className="rounded-b-[2rem]" />
                <div className="flex flex-col items-center justify-center h-[calc(100vh-80px)]">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
                    <span className="text-gray-400 font-oswald uppercase tracking-widest font-bold">Carregando dashboard...</span>
                </div>
            </div>
        );
    }

    const maxCount = getMaxCount();

    return (
        <div className="min-h-screen bg-gradient-to-b from-[#1e3a5f] to-black">
            <Header title="Dashboard" compact className="rounded-b-[2rem]" />

            <div className="p-4">
                {/* Cards de Estatísticas */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                    {/* Card Contatos */}
                    <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl shadow-lg p-7 hover:shadow-xl transition-all duration-300 group hover:border-white/20 relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-24 h-24 bg-blue-600/5 rounded-full blur-3xl -mr-12 -mt-12 group-hover:bg-blue-600/10 transition-colors"></div>
                        <div className="flex items-center justify-between relative z-10">
                            <div>
                                <p className="text-gray-400 text-[10px] font-bold mb-2 tracking-widest font-oswald uppercase">CONTATOS</p>
                                <p className="text-4xl font-bold text-white font-oswald tracking-tight group-hover:scale-105 transition-transform origin-left">{stats?.contacts || 0}</p>
                            </div>
                            <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/20 rounded-2xl p-4 shadow-lg border border-blue-500/30 group-hover:scale-110 transition-transform duration-300">
                                <svg className="w-8 h-8 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                            </div>
                        </div>
                    </div>

                    {/* Card Mensagens Enviadas */}
                    <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl shadow-lg p-7 hover:shadow-xl transition-all duration-300 group hover:border-white/20 relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-24 h-24 bg-green-600/5 rounded-full blur-3xl -mr-12 -mt-12 group-hover:bg-green-600/10 transition-colors"></div>
                        <div className="flex items-center justify-between relative z-10">
                            <div>
                                <p className="text-gray-400 text-[10px] font-bold mb-2 tracking-widest font-oswald uppercase">MENSAGENS ENVIADAS</p>
                                <p className="text-4xl font-bold text-white font-oswald tracking-tight group-hover:scale-105 transition-transform origin-left">{stats?.messagesSent || 0}</p>
                            </div>
                            <div className="bg-gradient-to-br from-green-500/20 to-green-600/20 rounded-2xl p-4 shadow-lg border border-green-500/30 group-hover:scale-110 transition-transform duration-300">
                                <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                        </div>
                    </div>

                    {/* Card Caixas de Entrada */}
                    <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl shadow-lg p-7 hover:shadow-xl transition-all duration-300 group hover:border-white/20 relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-24 h-24 bg-purple-600/5 rounded-full blur-3xl -mr-12 -mt-12 group-hover:bg-purple-600/10 transition-colors"></div>
                        <div className="flex items-center justify-between relative z-10">
                            <div>
                                <p className="text-gray-400 text-[10px] font-bold mb-2 tracking-widest font-oswald uppercase">CONEXÕES ATIVAS</p>
                                <p className="text-4xl font-bold text-white font-oswald tracking-tight group-hover:scale-105 transition-transform origin-left">{stats?.connections || 0}</p>
                            </div>
                            <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/20 rounded-2xl p-4 shadow-lg border border-purple-500/30 group-hover:scale-110 transition-transform duration-300">
                                <svg className="w-8 h-8 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    {/* Volume de Mensagens - Gráfico de Barras */}
                    <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl shadow-lg p-8 hover:border-white/20 transition-all relative overflow-hidden group">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-blue-600/5 rounded-full blur-3xl -mr-16 -mt-16 group-hover:bg-blue-600/10 transition-colors"></div>
                        <div className="flex items-center justify-between mb-8 relative z-10">
                            <div>
                                <h3 className="text-xl font-bold text-white font-oswald tracking-wide uppercase">VOLUME DE MENSAGENS</h3>
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">Últimos 7 dias</p>
                            </div>
                            <div className="bg-blue-600/20 text-blue-400 p-2.5 rounded-xl border border-blue-500/30 shadow-lg shadow-blue-900/20">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                </svg>
                            </div>
                        </div>

                        <div className="space-y-4 relative z-10">
                            {messagesPerDay.map((item, index) => (
                                <div key={index} className="flex items-center gap-4 group/item">
                                    <span className="text-[10px] font-bold text-gray-400 w-12 text-right flex-shrink-0 font-oswald uppercase tracking-widest">
                                        {formatDate(item.date)}
                                    </span>
                                    <div className="flex-1 bg-white/5 rounded-2xl h-10 relative overflow-hidden backdrop-blur-sm border border-white/10 group-hover/item:border-white/20 transition-all">
                                        <div
                                            className="bg-gradient-to-r from-blue-600 to-blue-400 h-full rounded-2xl transition-all duration-1000 ease-out flex items-center justify-end pr-3 shadow-[0_0_15px_rgba(37,99,235,0.3)]"
                                            style={{ width: `${(item.count / maxCount) * 100}%` }}
                                        >
                                            {item.count > 0 && (
                                                <span className="text-[10px] font-bold text-white drop-shadow-md font-oswald uppercase tracking-widest">
                                                    {item.count}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Últimas Campanhas Enviadas */}
                    <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl shadow-lg p-8 hover:border-white/20 transition-all relative overflow-hidden group">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-orange-600/5 rounded-full blur-3xl -mr-16 -mt-16 group-hover:bg-orange-600/10 transition-colors"></div>
                        <div className="flex items-center justify-between mb-8 relative z-10">
                            <div>
                                <h3 className="text-xl font-bold text-white font-oswald tracking-wide uppercase">ÚLTIMAS CAMPANHAS</h3>
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">Campanhas mais recentes</p>
                            </div>
                            <div className="bg-orange-600/20 text-orange-400 p-2.5 rounded-xl border border-orange-500/30 shadow-lg shadow-orange-900/20">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                                </svg>
                            </div>
                        </div>

                        <div className="space-y-3 relative z-10">
                            {recentCampaigns.length === 0 ? (
                                <div className="text-center py-16 text-gray-400 bg-white/5 rounded-3xl border border-white/10 border-dashed">
                                    <div className="bg-white/5 rounded-2xl w-20 h-20 flex items-center justify-center mx-auto mb-4 border border-white/10 shadow-lg">
                                        <svg className="w-10 h-10 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                                        </svg>
                                    </div>
                                    <p className="font-bold font-oswald uppercase tracking-widest text-sm">Nenhuma campanha recente</p>
                                    <p className="text-xs text-gray-500 mt-2">Suas campanhas aparecerão aqui</p>
                                </div>
                            ) : (
                                recentCampaigns.map((campaign) => (
                                    <div key={campaign.id} className="group border border-white/10 bg-white/5 hover:bg-white/10 rounded-2xl p-5 transition-all duration-300 hover:shadow-lg hover:border-white/20">
                                        <div className="flex items-start justify-between mb-4">
                                            <div className="flex items-center gap-4 flex-1">
                                                <div className="bg-gradient-to-br from-blue-500 to-blue-700 rounded-2xl p-3 flex-shrink-0 shadow-lg text-white transform group-hover:scale-110 transition-transform duration-300">
                                                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                                        <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                                                        <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                                                    </svg>
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <h4 className="font-bold text-white truncate font-oswald text-lg tracking-wide">{campaign.nome}</h4>
                                                    <p className="text-xs text-gray-400 font-medium mt-1 uppercase tracking-wider">
                                                        {formatDateTime(campaign.criadoEm)}
                                                    </p>
                                                </div>
                                            </div>
                                            <span className={`text-[10px] px-3 py-1.2 rounded-full font-bold uppercase tracking-widest border ${getStatusColor(campaign.status)}`}>
                                                {getStatusLabel(campaign.status)}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-6 text-xs font-bold text-gray-400 mt-4 pl-1 font-oswald uppercase tracking-wider">
                                            <div className="flex items-center gap-2 group/icon">
                                                <div className="p-1.5 rounded-lg bg-white/5 border border-white/10 group-hover/icon:bg-white/10 transition-colors">
                                                    <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                                    </svg>
                                                </div>
                                                <span>{campaign.totalContacts} contatos</span>
                                            </div>
                                            <div className="flex items-center gap-2 text-green-400 group/icon">
                                                <div className="p-1.5 rounded-lg bg-green-500/10 border border-green-500/20 group-hover/icon:bg-green-500/20 transition-colors">
                                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                    </svg>
                                                </div>
                                                <span>{campaign.sentCount} enviadas</span>
                                            </div>
                                            {campaign.failedCount > 0 && (
                                                <div className="flex items-center gap-2 text-red-400 group/icon">
                                                    <div className="p-1.5 rounded-lg bg-red-500/10 border border-red-500/20 group-hover/icon:bg-red-500/20 transition-colors">
                                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                        </svg>
                                                    </div>
                                                    <span>{campaign.failedCount} falhas</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
